import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';

/** Verified from alphalibrallc.com portfolio — Hub footer only links live brands. */
const ecosystemLinks = [
  { label: 'Alpha Libra LLC', href: 'https://alphalibrallc.com/' },
  { label: 'Tripnovac', href: 'https://tripnovac.com/' },
  { label: 'EZTaxNG', href: 'https://alphalibrallc.com/' },
  { label: 'Miraluc', href: 'https://alphalibrallc.com/' },
  { label: 'Mami African Hub', href: 'https://alphalibrallc.com/' },
];

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-wine-900 via-wine-800 to-wine-900 text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 items-start">

          {/* Brand */}
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="flex items-center mb-2">
              <img
                loading="lazy"
                decoding="async"
                src="/tripnovac-logo.jpeg"
                alt="Tripnovac"
                className="h-8 w-auto object-contain rounded-lg"
              />
            </Link>
            <p className="text-wine-300 text-[11px] mb-2 max-w-[240px] leading-relaxed">
              Tripnovac Travel Hub — community forums, visa experiences, local directory, deals &amp; events, and AI chat for travelers.
            </p>
            <p className="text-wine-400 text-[11px] mb-2 leading-relaxed">
              A product of Alpha Libra LLC · New Jersey, USA · Est. May 2024
            </p>
            <div className="flex items-center gap-1 text-[11px] text-wine-300 mb-2">
              <Mail className="h-3 w-3 text-gold-400 flex-shrink-0" />
              <a href="mailto:support@tripnovac.com" className="hover:text-gold-400 transition-colors">
                support@tripnovac.com
              </a>
            </div>
          </div>

          {/* Platform */}
          <div>
            <h3 className="font-semibold text-white text-[10px] mb-1.5 uppercase tracking-wider">Platform</h3>
            <ul className="space-y-1">
              {[
                { label: 'Forums', to: '/forums' },
                { label: 'Visa Experiences', to: '/visa-experiences' },
                { label: 'Directory', to: '/directory' },
                { label: 'Deals', to: '/deals' },
                { label: 'Events', to: '/events' },
                { label: 'AI Assistant', to: '/ai-assistant' },
                { label: 'Pricing', to: '/pricing' },
              ].map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-wine-300 hover:text-gold-400 text-[11px] transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Business */}
          <div>
            <h3 className="font-semibold text-white text-[10px] mb-1.5 uppercase tracking-wider">For Business</h3>
            <ul className="space-y-1">
              {[
                { label: 'List Your Business', to: '/directory/register' },
                { label: 'Pricing', to: '/pricing' },
                { label: 'Create a Deal', to: '/deals/create' },
                { label: 'Host an Event', to: '/events/create' },
              ].map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-wine-300 hover:text-gold-400 text-[11px] transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources + Legal */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <h3 className="font-semibold text-white text-[10px] mb-1.5 uppercase tracking-wider">Resources</h3>
              <ul className="space-y-1">
                {[
                  { label: 'Help Center', to: '/help' },
                  { label: 'Contact Us', to: '/contact' },
                ].map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-wine-300 hover:text-gold-400 text-[11px] transition-colors">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white text-[10px] mb-1.5 uppercase tracking-wider">Legal</h3>
              <ul className="space-y-1">
                {[
                  { label: 'Privacy', to: '/privacy' },
                  { label: 'Terms', to: '/terms' },
                  { label: 'Cookies', to: '/cookies' },
                  { label: 'Delete account', to: '/delete-account' },
                ].map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-wine-300 hover:text-gold-400 text-[11px] transition-colors">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Ecosystem */}
          <div>
            <h3 className="font-semibold text-white text-[10px] mb-1.5 uppercase tracking-wider">Ecosystem</h3>
            <ul className="space-y-1">
              {ecosystemLinks.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-wine-300 hover:text-gold-400 text-[11px] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-wine-700/60">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-col sm:flex-row justify-between items-center gap-1">
          <p className="text-wine-400 text-[11px]">
            © {currentYear} Tripnovac Travel Hub. All rights reserved.
          </p>
          <p className="text-wine-500 text-[11px]">
            Built by{' '}
            <a
              href="https://alphalibrallc.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-wine-300 transition-colors"
            >
              Alpha Libra LLC
            </a>
            {' '}· New Jersey · Est. May 2024 · Part of the Tripnovac ecosystem
          </p>
        </div>
      </div>
    </footer>
  );
}
